/**
 * MessageLimitModal Component
 *
 * Modal that appears when a conversation exceeds the message limit.
 * Recommends starting a new chat for better performance.
 */

import React, { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Sparkles, PlusCircle } from 'lucide-react';
import { createTranslator } from '../../../utils/detectLanguage';
import { messageLimitModalPtBR } from '../../mappings/messageLimitModal.pt-br';
import { messageLimitModalES } from '../../mappings/messageLimitModal.es';

const MessageLimitModal = memo(({ isVisible, messageCount, onContinue, onNewChat, onClose }) => {
  // Translation helper
  const t = createTranslator({
    'pt-br': messageLimitModalPtBR,
    'es': messageLimitModalES
  });

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        >
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-filter backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 border border-amber-500/30 rounded-xl shadow-2xl p-6 w-full max-w-md relative backdrop-filter backdrop-blur-lg"
            style={{
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(245, 158, 11, 0.1), 0 0 40px 5px rgba(245, 158, 11, 0.05)',
            }}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <motion.div
                  className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  <MessageCircle className="w-5 h-5 text-amber-400" />
                </motion.div>
                <div>
                  <h3 className="text-amber-300 font-semibold text-lg">{t('Long Conversation')}</h3>
                  <p className="text-amber-200/70 text-sm">{messageCount} {t('messages in this chat')}</p>
                </div>
              </div>
              <motion.button
                onClick={onClose}
                className="p-1.5 rounded-full text-slate-400 hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.1, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                whileTap={{ scale: 0.9 }}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent mb-5 relative">
              <div className="absolute inset-0 blur-sm bg-gradient-to-r from-transparent via-amber-400/20 to-transparent" />
            </div>

            <div className="space-y-4 mb-6">
              <p className="text-slate-200 text-sm leading-relaxed">
                {t('This conversation has grown quite long! For better performance and organization, we recommend starting a new chat.')}
              </p>

              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium mb-1">
                  <Sparkles className="w-4 h-4" />
                  {t('Benefits of starting fresh:')}
                </div>
                <ul className="text-slate-300 text-xs space-y-1 ml-6">
                  <li>• {t('Faster response times')}</li>
                  <li>• {t('Better conversation focus')}</li>
                  <li>• {t('Easier to find specific topics')}</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <motion.button
                onClick={onContinue}
                className="flex-1 py-2.5 px-4 bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-sm font-medium border border-slate-600/50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {t('Continue Here')}
              </motion.button>
              <motion.button
                onClick={onNewChat}
                className="flex-1 py-2.5 px-4 bg-gradient-to-r from-emerald-500/20 to-amber-500/20 hover:from-emerald-500/30 hover:to-amber-500/30 text-emerald-300 rounded-lg transition-all text-sm font-medium border border-emerald-500/30 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <PlusCircle className="w-4 h-4" />
                {t('Start New Chat')}
              </motion.button>
            </div>

            <motion.p
              className="text-xs text-slate-500 text-center mt-4"
              initial={{ opacity: 0.6 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
            >
              {t('You can always return to this conversation later')}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

MessageLimitModal.displayName = 'MessageLimitModal';

export default MessageLimitModal;
